/**
 * Per-row messages on the ticket line-item editors, which are keyed by ROW INDEX.
 *
 * Both editors hold their validation errors and notes keyed against the position
 * of the row in `lineItems` — a plain note is a string, a server refusal is a
 * list of reasons, and this helper is generic so both re-key through it. That
 * works right up until a row is deleted, at which point every row after it
 * shifts down one and every message is attached to the wrong line — a red ring
 * and "Description is required" on a row that has one, while the row that
 * actually failed looks fine.
 *
 * The honest fix would be a stable id per row, but line items have none until
 * the server assigns one on save, and inventing one changes the payload shape
 * that four screens and the offline queue all read. Re-keying on delete is the
 * containable version: it is exact, it is the only mutation that shifts indices,
 * and it lives in one place both editors call.
 */

/**
 * Re-key an index-keyed message map after the row at `removed` is deleted.
 *
 * The removed row's own message goes with it; everything after it moves down one.
 * Anything before it is untouched.
 */
export function reindexAfterRemoval<T>(
  notes: Record<number, T>,
  removed: number
): Record<number, T> {
  const next: Record<number, T> = {};
  for (const [key, message] of Object.entries(notes)) {
    const index = Number(key);
    if (index === removed) continue;
    next[index > removed ? index - 1 : index] = message;
  }
  return next;
}
